import React, { useEffect, useState } from "react";
import {offerDelivery, offerDeliveryFailure, offerResponse} from './offerDelivery';
import RequestTerms, { RESPONSE_MEANINGS, REACHED_MEANINGS, REQUEST_STATUS_MEANINGS } from "./requestTerms";
import {
  Box, Button, Flex, Table, Tbody, Td, Th, Thead, Tr, Text, useToast, Spinner, Badge,
  Select, Heading, HStack, IconButton, Tooltip, SimpleGrid, Divider, Alert,
  AlertIcon, Switch, FormLabel,
} from "@chakra-ui/react";
import { InfoOutlineIcon, ArrowBackIcon, WarningTwoIcon } from "@chakra-ui/icons";
import {
  getRequests, getRequestDetail, getDispatchPause, setDispatchPause, cancelJob,
} from "../../utils/apis/agentApi";

/** Every request, however it was served.
 *
 *  A request reaches a provider one of two ways. A CONNECTION: we hand the
 *  customer a provider's number and the customer makes contact. A DISPATCH: the
 *  customer asks us to reach out on their behalf and providers are offered the
 *  job. They live in different tables; support asks the same questions of both,
 *  so the agent unifies them and this renders one list.
 */
const STATUS_COLOR: Record<string, string> = {
  app_message_sent: "blue",
  searching: "blue", connected: "gray", assigned: "green", in_contact: "purple",
  no_contact: "red", completed: "green", cancelled: "orange", unfilled: "red",
  unknown: "gray", contact_pending: "orange", needs_details: "blue",
  verification_required: "orange", contact_unavailable: "red", classification_blocked: "orange", lookup_failed: "red",
};

const label = (s: string) => s === "app_message_sent" ? "Message sent" : (s || "").replace(/_/g, " ");
const fmt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const stars = (n?: number | null) => (n ? "⭐".repeat(n) + ` ${n}/5` : "—");
const yesNo = (v: any) =>
  v === true ? <Text as="span" color="green.600" fontWeight="600">Yes</Text>
  : v === false ? <Text as="span" color="red.500" fontWeight="600">No</Text>
  : <Text as="span" color="admin.muted">not answered</Text>;

const Requests = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [dailyCounts, setDailyCounts] = useState<{date: string; count: number}[]>([]);
  const [total, setTotal] = useState(0);
  const [statuses, setStatuses] = useState<Record<string, string>>({});
  const [sorts, setSorts] = useState<string[]>([]);
  const [sort, setSort] = useState("newest");
  const [status, setStatus] = useState("");
  const [groupDuplicates, setGroupDuplicates] = useState(true);
  const [legend, setLegend] = useState(false);
  const [colKey, setColKey] = useState<string | null>(null);
  const [pause, setPause] = useState<any>(null);
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const fail = (res: any) =>
    toast({ title: res.status === 502 ? "Agent unreachable" : "Failed to load",
      status: "error", duration: 3000, isClosable: true });

  const load = async () => {
    setLoading(true);
    const res = await getRequests(sort, status || undefined, 200, groupDuplicates);
    if (res.ok && res.data) {
      setRows(res.data.requests || []);
      setDailyCounts(res.data.daily_counts || []);
      setTotal(res.data.total ?? (res.data.requests || []).length);
      setStatuses(res.data.statuses || {});
      setSorts(res.data.sorts || []);
    } else fail(res);
    setLoading(false);
  };
  const loadPause = async () => {
    const res = await getDispatchPause();
    if (res.ok && res.data) setPause(res.data);
  };
  useEffect(() => {
    load(); loadPause();
    // Recruitment credits may be assigned after the customer's first request.
    const refresh = () => { if (document.visibilityState === 'visible') void load(); };
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [sort, status, groupDuplicates]); // eslint-disable-line

  /* Stopping dispatch reaches real providers, so it asks first and records who
     did it and why. It stops NEW offers only: anyone who already accepted still
     gets their customer, and checks and reviews still run. */
  const togglePause = async (next: boolean) => {
    const note = next
      ? window.prompt("Why are you stopping new job offers? (shown in the panel)") ?? ""
      : "";
    if (next && note === null) return;
    if (next && !window.confirm(
      "Stop sending NEW job offers?\n\nJobs already accepted are unaffected: those " +
      "providers still get their customer, and contact checks and reviews still run.")) return;
    const res = await setDispatchPause(next, note || undefined);
    if (res.ok) {
      toast({ title: next ? "New job offers stopped" : "Dispatch resumed",
        status: next ? "warning" : "success", duration: 3000, isClosable: true });
      loadPause();
    } else toast({ title: "Could not change it", status: "error", duration: 3000 });
  };

  const open = async (kind: string, id: string) => {
    setLoading(true);
    const res = await getRequestDetail(kind, id);
    if (res.ok && res.data) setDetail({...res.data,
      related_requests: rows.find(r => r.kind === kind && r.id === id)?.related_requests || []});
    else fail(res);
    setLoading(false);
  };

  const row = (k: string, v: any) => (
    <Flex py={2} borderBottom="1px solid" borderColor="gray.100" gap={{base: 1, md: 4}} direction={{base: "column", md: "row"}}>
      <Text minW={{base: 0, md: "230px"}} color="admin.muted" fontSize="sm">{k}</Text>
      <Box fontSize="sm" minW={0} overflowWrap="anywhere">{v}</Box>
    </Flex>
  );

  if (detail) {
    return (
      <Box p={5}>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Button leftIcon={<ArrowBackIcon />} size="sm" onClick={() => setDetail(null)}>
            All requests
          </Button>
          {/* Only a dispatch that is still out looking can be stopped. A
              connection has nothing running to stop, and a finished job has
              nothing to cancel. */}
          {detail.kind === "dispatch" && ["queued", "dispatching", "assigned"].includes(detail.raw_status || detail.status) && (
            <Button size="sm" colorScheme="red" variant="outline" onClick={async () => {
              if (!window.confirm(
                "Stop this request?\n\nNo further providers will be contacted for it.")) return;
              const res = await cancelJob(detail.id);
              if (res.ok) {
                toast({ title: "Request stopped", status: "success", duration: 2500 });
                open(detail.kind, detail.id);
              } else toast({ title: "Could not stop it", status: "error", duration: 3000 });
            }}>
              Stop this request
            </Button>
          )}
        </Flex>
        <HStack mb={3} flexWrap="wrap">
          <Heading size="md">{detail.reference}</Heading>
          <Badge colorScheme={STATUS_COLOR[detail.status] || "gray"}>{label(detail.status)}</Badge>
          {detail.reached_on_behalf && <Badge colorScheme="purple">on behalf</Badge>}
        </HStack>
        <RequestTerms />
        {detail.app_communication && <Box my={3} p={3} borderWidth="1px" borderRadius="md">
          <Badge colorScheme="purple">In-app connection</Badge>
          <Text mt={2}>Customer: {detail.app_communication.customer_messages} messages · Provider: {detail.app_communication.provider_messages} messages</Text>
          <Text fontSize="sm">Last message: {fmt(detail.app_communication.last_message_at)}</Text>
          <Text fontSize="sm" mb={2}>Communication recorded; booking and completion are not confirmed.</Text>
          {detail.app_communication.chat_ids.map((id: string, i: number) => <Button key={id} as="a" size="sm" mr={2} href={`/app-chats?chat=${encodeURIComponent(id)}`}>View conversation{detail.app_communication.chat_ids.length > 1 ? ` ${i + 1}` : ""}</Button>)}
        </Box>}
        {detail.status_reason && <Alert status="warning" mb={3} borderRadius="md"><AlertIcon /><Text fontSize="sm">{detail.status_reason}</Text></Alert>}
        {row("Date", fmt(detail.date))}
        {row("Customer", detail.customer?.name || detail.customer?.phone || "—")}
        {row("Service", detail.service || "—")}
        {row("Location", detail.location || "Not captured")}
        {row("Brief", detail.brief || "—")}
        {detail.related_requests?.length > 0 && row("Grouped contact records", <Box>
          <Text mb={2}>These earlier records were grouped as repeat contacts. Each original record remains available.</Text>
          {detail.related_requests.map((r: any) => <Button key={r.id} size="xs" variant="link" mr={3}
            onClick={() => open(r.kind, r.id)}>{r.reference} · {fmt(r.created_at)}</Button>)}
        </Box>)}
        {row("Asked us to reach out on their behalf",
          detail.paths?.length > 1
            ? "Both: contacts were shared, then we reached out too"
            : detail.app_communication ? "No, direct in-app communication" : detail.reached_on_behalf ? "Yes" : detail.kind === "search" ? "No contact handover recorded" : "No, contacts were shared")}
        {row(detail.kind === "search" ? "Providers considered (not contacts shared)" : "Matched providers", detail.matched_providers)}
        {row("Provider selected", detail.provider_selected?.name || "—")}
        {detail.provider_changes?.length > 1 &&
          row("Provider changed", `${detail.provider_changes.length} assignments`)}
        {row("SP contacted (customer says)", yesNo(detail.sp_contacted))}
        {row("Provider responded", yesNo(detail.provider_responded))}
        {row("Service provider confirmed", yesNo(detail.provider_confirmed))}
        {row("Customer confirmed", yesNo(detail.customer_confirmed))}
        {row("Appointment", detail.appointment
          ? (typeof detail.appointment === "object" ? JSON.stringify(detail.appointment) : String(detail.appointment))
          : "—")}
        {row("Service completed", yesNo(detail.service_completed))}
        {row("Rating of SP (by customer)", stars(detail.rating_of_provider))}
        {row("Rating of customer (by SP)", stars(detail.rating_of_customer))}
        {row("Last update", fmt(detail.updated_at))}

        <Heading size="sm" mt={6} mb={2}>Providers</Heading>
        {colKey && (
          <Box mb={3} p={3} borderWidth="1px" borderRadius="md" bg="admin.subtle">
            <Text fontWeight="600" fontSize="sm" mb={2}>
              {colKey === "response" ? "Response" : "Provider says reached"}
            </Text>
            {(colKey === "response" ? RESPONSE_MEANINGS : REACHED_MEANINGS).map(([k, v]) => (
              <Flex key={k} py={1} gap={3} fontSize="sm" direction={{ base: "column", md: "row" }}>
                <Text minW={{ base: 0, md: "230px" }} fontWeight="500">{k}</Text>
                <Text color="admin.muted">{v}</Text>
              </Flex>
            ))}
          </Box>
        )}
        {(detail.providers || []).length === 0 ? <Text color="admin.muted" fontSize="sm">None.</Text> : (
          detail.app_communication ? <Box>{detail.providers.map((p: any) => <Text key={p.provider_id}>{p.name} {p.phone ? `· ${p.phone}` : ""}</Text>)}</Box> : <Box maxW="100%" overflowX="auto" role="region" aria-label="Providers table" tabIndex={0}>
          <Table size="sm" variant="simple">
            <Thead><Tr>
              <Th>Name</Th>
              <Th>
                <HStack spacing={1}>
                  <Text as="span">Response</Text>
                  <Tooltip label="What these mean">
                    <IconButton aria-label="Response meanings" size="xs" variant="ghost"
                      icon={<InfoOutlineIcon />}
                      onClick={() => setColKey(colKey === "response" ? null : "response")} />
                  </Tooltip>
                </HStack>
              </Th>
              <Th>WhatsApp delivery</Th>
              <Th>Customer says</Th>
              <Th>
                <HStack spacing={1}>
                  <Text as="span">Provider says reached</Text>
                  <Tooltip label="What these mean">
                    <IconButton aria-label="Reached meanings" size="xs" variant="ghost"
                      icon={<InfoOutlineIcon />}
                      onClick={() => setColKey(colKey === "reached" ? null : "reached")} />
                  </Tooltip>
                </HStack>
              </Th>
            </Tr></Thead>
            <Tbody>
              {detail.providers.map((p: any, i: number) => (
                <Tr key={i}>
                  <Td>{p.name || "—"}
                    {p.contact_clicks > 0 && <Text fontSize="xs" color="admin.muted">
                      {p.contact_clicks} contact click(s) · {fmt(p.last_contact_click)}
                    </Text>}
                  </Td>
                  <Td>
                    {offerResponse(p)}
                    {p.decline_reason ? ` (${label(p.decline_reason)})` : ""}
                  </Td><Td minW="250px" maxW="380px" whiteSpace="normal">
                    {offerDelivery(p)}
                    {offerDeliveryFailure(p) && (
                      <Text fontSize="xs" color="red.500" mt={1}>
                        {offerDeliveryFailure(p)}
                      </Text>
                    )}
                  </Td>
                  <Td>{p.status ? label(p.status) : "—"}</Td>
                  <Td>{p.sp_reached === true ? "yes" : p.sp_reached === false ? "no"
                    : p.sp_asked === "no_window" ? "not asked (no WhatsApp window)"
                    : p.sp_asked ? "no answer" : "not asked yet"}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          </Box>
        )}

        <Heading size="sm" mt={6} mb={2}>Progression</Heading>
        {(detail.progression || []).length === 0 ? (
          <Text color="admin.muted" fontSize="sm">Nothing recorded yet.</Text>
        ) : (
          <Box borderLeft="2px solid" borderColor="gray.200" pl={4} ml={1}>
            {detail.progression.map((sp: any, i: number) => (
              <Flex key={i} py={2} gap={4} fontSize="sm" alignItems="baseline"
                    borderBottom={i === detail.progression.length - 1 ? undefined : "1px solid"}
                    borderColor="gray.100">
                <Text color="admin.muted" minW="130px" flexShrink={0}>
                  {sp.at ? fmt(sp.at) : "—"}
                </Text>
                <Box>
                  <Text fontWeight="500">{sp.label}</Text>
                  {sp.detail && <Text color="admin.muted">{sp.detail}</Text>}
                </Box>
              </Flex>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box p={5}>
      <Flex justifyContent="space-between" mb={4} alignItems="center">
        <Heading size="md">Requests</Heading>
        <HStack spacing={4}>
          <HStack spacing={2} borderWidth="1px" borderRadius="md" px={3} py={1.5}>
            <FormLabel htmlFor="pauseSwitch" mb={0} fontSize="sm" color="admin.muted">
              Send new job offers
            </FormLabel>
            <Switch id="pauseSwitch" colorScheme="green"
              isChecked={!(pause?.paused)}
              isDisabled={pause === null || pause?.dispatch_configured === false}
              onChange={(e) => togglePause(!e.target.checked)} />
          </HStack>
          <Button colorScheme="purple" backgroundColor="#88077b" onClick={load}>Refresh</Button>
        </HStack>
      </Flex>

      <RequestTerms />

      {pause?.paused && (
        <Alert status="warning" mb={4} borderRadius="md">
          <AlertIcon as={WarningTwoIcon} />
          <Box fontSize="sm">
            <Text fontWeight="600">New job offers are stopped.</Text>
            <Text color="admin.muted">
              Jobs already accepted are unaffected.
              {pause.note ? ` Reason: ${pause.note}.` : ""}
              {pause.updated_by ? ` Stopped by ${pause.updated_by}` : ""}
              {pause.updated_at ? ` on ${fmt(pause.updated_at)}.` : ""}
            </Text>
          </Box>
        </Alert>
      )}

      {pause?.dispatch_configured === false && (
        <Alert status="info" mb={4} borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Dispatch is switched off for this deployment, so no offers are sent
            regardless of this control.
          </Text>
        </Alert>
      )}

      <Flex mb={4} gap={3} alignItems="center" flexWrap="wrap">
        <Select size="sm" w="190px" value={sort} onChange={(e) => setSort(e.target.value)}>
          {sorts.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </Select>
        <Select size="sm" w="190px" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">all statuses</option>
          {Object.keys(statuses).map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </Select>
        <Tooltip label="What each status means">
          <IconButton aria-label="Status meanings" size="sm" icon={<InfoOutlineIcon />}
            onClick={() => setLegend((v) => !v)} />
        </Tooltip>
        <Select aria-label="Repeated contacts" size="sm" w="220px" value={groupDuplicates ? "grouped" : "all"}
          onChange={e => setGroupDuplicates(e.target.value === "grouped")}>
          <option value="grouped">Group repeated contacts</option>
          <option value="all">Show separate contact records</option>
        </Select>
        <Text color="admin.muted" fontSize="sm">{total.toLocaleString()} request(s){total > rows.length ? ` · showing ${rows.length}` : ""}</Text>
      </Flex>

      {dailyCounts.length > 0 && (
        <Box mb={4} borderWidth="1px" borderRadius="md" p={3}>
          <Flex gap={4} flexWrap="wrap" alignItems="center">
            <Text fontWeight="600">Requests per day (Lagos)</Text>
            <Text fontSize="sm">Today: <strong>{dailyCounts[0].count.toLocaleString()}</strong></Text>
            <Text fontSize="sm">Yesterday: <strong>{(dailyCounts[1]?.count || 0).toLocaleString()}</strong></Text>
          </Flex>
          <Box as="details" mt={2}>
            <Box as="summary" cursor="pointer" fontSize="sm">View last 30 days</Box>
            <Text fontSize="xs" color="admin.muted" my={2}>By request creation date, using the selected status and contact grouping.</Text>
            <SimpleGrid columns={{base: 2, md: 5, lg: 10}} spacing={2}>
              {dailyCounts.map(day => <Box key={day.date} borderWidth="1px" borderRadius="md" p={2}>
                <Text fontSize="xs">{new Date(day.date + "T12:00:00+01:00").toLocaleDateString("en-GB", {day: "numeric", month: "short", timeZone: "Africa/Lagos"})}</Text>
                <Text fontWeight="700">{day.count.toLocaleString()}</Text>
              </Box>)}
            </SimpleGrid>
          </Box>
        </Box>
      )}

      {legend && (
        <Box mb={4} p={3} borderWidth="1px" borderRadius="md">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
            {Object.keys(statuses).map((k) => (
              <HStack key={k} align="start">
                <Badge colorScheme={STATUS_COLOR[k] || "gray"}>{label(k)}</Badge>
                <Text fontSize="sm" color="admin.muted">{REQUEST_STATUS_MEANINGS.find(([term]) => term === label(k))?.[1] || statuses[k]}</Text>
              </HStack>
            ))}
          </SimpleGrid>
          <Divider mt={3} />
          <Text fontSize="xs" color="admin.muted" mt={2}>
            "on behalf" means the customer asked us to contact the provider for them.
          </Text>
        </Box>
      )}

      {loading ? <Spinner size="xl" /> : rows.length === 0 ? (
        <Text color="admin.muted">No requests yet.</Text>
      ) : (
        <Box maxW="100%" overflowX="auto" role="region" aria-label="Requests table" tabIndex={0}>
        <Table variant="striped" size="sm">
          <Thead backgroundColor="#88077b"><Tr>
            <Th color="white">Request</Th>
            <Th color="white">Date</Th><Th color="white">Customer</Th><Th color="white">SP</Th>
            <Th color="white">Service</Th><Th color="white">Location</Th><Th color="white">Status</Th>
          </Tr></Thead>
          <Tbody>
            {rows.map((r) => (
              <Tr key={`${r.kind}-${r.id}`} cursor="pointer" _hover={{ bg: "gray.100" }}
                  onClick={() => open(r.kind, r.id)}>
                <Td fontFamily="mono" fontSize="xs">{r.reference}
                  {r.app_communication && <Badge display="block" width="fit-content" mt={1} colorScheme="purple">In-app connection</Badge>}
                  {r.related_requests?.length > 0 && <Badge ml={2} colorScheme="purple">
                    {r.related_requests.length} repeat(s) grouped
                  </Badge>}
                  {r.contact_clicks > 0 && <Text fontFamily="body" color="admin.muted" fontSize="xs">
                    {r.contact_clicks} contact click(s)
                  </Text>}
                </Td>
                <Td>
                  {fmt(r.created_at)}
                  {r.also_reference && (
                    <Text fontSize="0.7em" color="admin.muted">
                      {r.reference} + {r.also_reference}
                    </Text>
                  )}
                </Td>
                <Td>{r.customer_name || r.customer || "—"}</Td>
                <Td>{r.app_communication ? r.providers?.join(', ') : r.provider_selected || <Text as="span" color="admin.muted">{r.matched_providers} {r.kind === "search" ? "considered" : "matched"}</Text>}</Td>
                <Td>{r.service || "—"}</Td>
                <Td>{r.location || "Not captured"}</Td>
                <Td>
                  <Badge colorScheme={STATUS_COLOR[r.status] || "gray"}>{label(r.status)}</Badge>
                  {r.status_reason && <Text fontSize="xs" mt={1} maxW="300px">{r.status_reason}</Text>}
                  {r.reached_on_behalf && <Badge ml={1} colorScheme="purple">on behalf</Badge>}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        </Box>
      )}
    </Box>
  );
};

export default Requests;
